'use client';

import Icon from '@/components/ui/AppIcon';
import { JobBlock } from './CalendarInteractive';

interface JobBlockCardProps {
  job: JobBlock;
  compact: boolean;
  selected: boolean;
  onClick: () => void;
  onSelect: () => void;
  fullWidth?: boolean;
  isConflict?: boolean;
}

const statusColors: Record<string, string> = {
  'scheduled': 'bg-blue-500',
  'in-progress': 'bg-green-500',
  'completed': 'bg-gray-400',
  'delayed': 'bg-amber-500',
  'cancelled': 'bg-red-400',
};

const statusBg: Record<string, string> = {
  'scheduled': 'bg-blue-50 border-blue-200 hover:bg-blue-100',
  'in-progress': 'bg-green-50 border-green-200 hover:bg-green-100',
  'completed': 'bg-gray-50 border-gray-200 hover:bg-gray-100',
  'delayed': 'bg-amber-50 border-amber-200 hover:bg-amber-100',
  'cancelled': 'bg-red-50 border-red-200 hover:bg-red-100',
};

const priorityIcon: Record<string, string> = {
  high: 'ExclamationCircleIcon',
  medium: 'MinusCircleIcon',
  low: 'CheckCircleIcon',
};

const priorityColor: Record<string, string> = {
  high: 'text-error',
  medium: 'text-warning',
  low: 'text-success',
};

function getDeadlineDays(endDate?: string | null): number | null {
  if (!endDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function DeadlineBadge({ days }: { days: number }) {
  if (days < 0) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold bg-red-600 text-white leading-none">
        <Icon name="ExclamationCircleIcon" size={8} />
        {Math.abs(days)}d overdue
      </span>
    );
  }
  if (days === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold bg-orange-500 text-white leading-none">
        <Icon name="ClockIcon" size={8} />
        Due today
      </span>
    );
  }
  if (days <= 2) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold bg-orange-400 text-white leading-none">
        <Icon name="ClockIcon" size={8} />
        {days}d left
      </span>
    );
  }
  if (days <= 5) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-semibold bg-yellow-400 text-yellow-900 leading-none">
        <Icon name="ClockIcon" size={8} />
        {days}d left
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-semibold bg-gray-200 text-gray-600 leading-none">
      <Icon name="ClockIcon" size={8} />
      {days}d left
    </span>
  );
}

export default function JobBlockCard({ job, compact, selected, onClick, onSelect, fullWidth, isConflict }: JobBlockCardProps) {
  const deadlineDays = getDeadlineDays(job.endDate);

  if (compact) {
    return (
      <div
        className={`flex items-center gap-1 px-1.5 py-0.5 rounded-sm cursor-pointer border transition-standard ${
          selected ? 'ring-2 ring-primary ring-offset-1' : ''
        } ${isConflict ? 'ring-2 ring-red-500 ring-offset-1 border-red-400' : statusBg[job.status]}`}
        onClick={onClick}
      >
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusColors[job.status]}`} />
        <span className="font-caption text-xs text-foreground truncate">{job.customer}</span>
        {isConflict && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" title="Double-booked" />}
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-md border cursor-pointer transition-standard group ${fullWidth ? 'w-full' : 'w-full'} ${
        selected ? 'ring-2 ring-primary ring-offset-1' : ''
      } ${isConflict ? 'ring-2 ring-red-500 ring-offset-1 border-red-400 bg-red-50 hover:bg-red-100' : statusBg[job.status]}`}
      onClick={onClick}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md ${isConflict ? 'bg-red-500' : statusColors[job.status]}`} />
      <div className="pl-2.5 pr-2 py-1.5">
        <div className="flex items-start justify-between gap-1">
          <div className="flex-1 min-w-0">
            <p className="font-body text-xs font-semibold text-foreground truncate">{job.customer}</p>
            <p className="font-caption text-[10px] text-muted-foreground truncate">{job.title}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Icon name={priorityIcon[job.priority] as any} size={12} className={priorityColor[job.priority]} variant="solid" />
            <input
              type="checkbox"
              checked={selected}
              onChange={e => { e.stopPropagation(); onSelect(); }}
              onClick={e => e.stopPropagation()}
              className="w-3 h-3 accent-primary opacity-0 group-hover:opacity-100 transition-standard"
              aria-label={`Select job ${job.customer}`}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="font-caption text-[10px] text-muted-foreground flex items-center gap-0.5">
            <Icon name="UsersIcon" size={10} />
            {job.crew}
          </span>
          <span className="font-caption text-[10px] text-muted-foreground">
            {job.startHour < 12 ? `${job.startHour}AM` : `${job.startHour - 12}PM`} · {job.duration}h
          </span>
          {isConflict && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold bg-red-600 text-white leading-none">
              <Icon name="ExclamationCircleIcon" size={8} />
              Double-booked
            </span>
          )}
          {deadlineDays !== null && <DeadlineBadge days={deadlineDays} />}
        </div>
      </div>
    </div>
  );
}